/**
 * Estimate Hooks - T179
 *
 * TanStack Query hooks for cost estimate CRUD operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { CreateEstimateInput, UpdateEstimateInput } from '@armard/shared';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export type EstimateStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted';

export interface EstimateItem {
  id: string;
  estimateId: string;
  position: number;
  description: string;
  longDescription: string | null;
  quantity: string;
  unit: string;
  unitPrice: string;
  total: string;
}

export interface Customer {
  id: string;
  customerNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
}

export interface Estimate {
  id: string;
  estimateNumber: string;
  customerId: string;
  status: EstimateStatus;
  issueDate: string;
  validUntil: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  subject: string | null;
  customerReference: string | null;
  introText: string | null;
  closingText: string | null;
  netTotal: string;
  vatAmount: string;
  grossTotal: string;
  vatRate: string;
  invoiceId: string | null;
  createdAt: string;
  updatedAt: string;
  customer?: Customer;
  items?: EstimateItem[];
}

export interface EstimateListQuery {
  page?: number;
  limit?: number;
  status?: EstimateStatus;
  customerId?: string;
  search?: string;
  sortBy?: 'issueDate' | 'estimateNumber' | 'grossTotal' | 'validUntil';
  sortOrder?: 'asc' | 'desc';
}

export interface EstimateListResult {
  estimates: Estimate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Status helpers
export const statusLabels: Record<EstimateStatus, string> = {
  draft: 'Entwurf',
  sent: 'Versendet',
  accepted: 'Angenommen',
  rejected: 'Abgelehnt',
  expired: 'Abgelaufen',
  converted: 'Umgewandelt',
};

export const statusColors: Record<EstimateStatus, string> = {
  draft: 'bg-gray-500/20 text-gray-400',
  sent: 'bg-blue-500/20 text-blue-400',
  accepted: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  expired: 'bg-orange-500/20 text-orange-400',
  converted: 'bg-purple-500/20 text-purple-400',
};

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

const estimateKeys = {
  all: ['estimates'] as const,
  lists: () => [...estimateKeys.all, 'list'] as const,
  list: (query: EstimateListQuery) => [...estimateKeys.lists(), query] as const,
  details: () => [...estimateKeys.all, 'detail'] as const,
  detail: (id: string) => [...estimateKeys.details(), id] as const,
};

// ─────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * List estimates with pagination and filters
 */
export function useEstimates(query: EstimateListQuery = {}) {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.status) params.set('status', query.status);
  if (query.customerId) params.set('customerId', query.customerId);
  if (query.search) params.set('search', query.search);
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortOrder) params.set('sortOrder', query.sortOrder);

  const queryString = params.toString();

  return useQuery({
    queryKey: estimateKeys.list(query),
    queryFn: () => api.get<EstimateListResult>(`/estimates${queryString ? `?${queryString}` : ''}`),
  });
}

/**
 * Get single estimate by ID
 */
export function useEstimate(id: string) {
  return useQuery({
    queryKey: estimateKeys.detail(id),
    queryFn: () => api.get<Estimate>(`/estimates/${id}`),
    enabled: !!id,
  });
}

/**
 * Create new estimate
 */
export function useCreateEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEstimateInput) =>
      api.post<Estimate>('/estimates', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
    },
  });
}

/**
 * Update estimate
 */
export function useUpdateEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateEstimateInput & { id: string }) =>
      api.patch<Estimate>(`/estimates/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      queryClient.setQueryData(estimateKeys.detail(data.id), data);
    },
  });
}

/**
 * Delete estimate
 */
export function useDeleteEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/estimates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
    },
  });
}

/**
 * Mark estimate as sent
 */
export function useSendEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<Estimate>(`/estimates/${id}/send`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      queryClient.setQueryData(estimateKeys.detail(data.id), data);
    },
  });
}

/**
 * Mark estimate as accepted
 */
export function useAcceptEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<Estimate>(`/estimates/${id}/accept`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      queryClient.setQueryData(estimateKeys.detail(data.id), data);
    },
  });
}

/**
 * Mark estimate as rejected
 */
export function useRejectEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<Estimate>(`/estimates/${id}/reject`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      queryClient.setQueryData(estimateKeys.detail(data.id), data);
    },
  });
}

/**
 * Convert estimate to invoice
 */
export function useConvertEstimate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ id: string; invoiceNumber: string }>(`/estimates/${id}/convert`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: estimateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Get estimate PDF URL
 */
export function getEstimatePdfUrl(id: string): string {
  return `/api/v1/estimates/${id}/pdf`;
}
